import { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../state/AuthContext";
import { DEMO_COMPANY_HFS, SEED_CHARGE_CODES } from "../data/timekeepingSeed";
import type {
  ChargeCode,
  ChargeCodeClass,
  ChargeCodeLifecycle,
  LaborType,
} from "../lib/timekeeping";

type FactoryTab = "leave" | "bp" | "indirect" | "contract" | "catalog";

const JOB_AUTO_BP = ["Director", "VP", "Growth Analyst"] as const;

function fmtMoney(n?: number) {
  if (n == null) return "—";
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

export function ChargeCodesPage() {
  const { moduleIds, homePath, roleIds } = useAuth();
  const [codes, setCodes] = useState<ChargeCode[]>(() => [...SEED_CHARGE_CODES]);
  const [tab, setTab] = useState<FactoryTab>("catalog");
  const [bpAutoGrant, setBpAutoGrant] = useState<Record<string, boolean>>({
    Director: true,
    VP: true,
    "Growth Analyst": true,
  });
  const [flash, setFlash] = useState("");

  // Leave draft form
  const [leaveCode, setLeaveCode] = useState("PT-ADMIN");
  const [leaveTitle, setLeaveTitle] = useState("Administrative leave");
  const [leaveBin, setLeaveBin] = useState<LaborType>("PT");
  const [leaveAlias, setLeaveAlias] = useState("");

  // B&P request
  const [bpTitle, setBpTitle] = useState("Pursuit — Agency X");
  const [bpReason, setBpReason] = useState("");

  // G&A
  const [gaTitle, setGaTitle] = useState("G&A — Facilities");

  // Contract draft
  const [vv, setVv] = useState("01");
  const [ccc, setCcc] = useState("017");
  const [per, setPer] = useState("000");
  const [clin, setClin] = useState("0001");
  const [task, setTask] = useState("ENG");
  const [award, setAward] = useState("750000");
  const [ceiling, setCeiling] = useState("800000");
  const [funded, setFunded] = useState("400000");
  const [contractTitle, setContractTitle] = useState("New award — Engineering");
  const [pricingType, setPricingType] = useState("CPFF");

  const isVpHr = roleIds.includes("ROLE-VP-HR");
  const isHrAnalyst = roleIds.includes("ROLE-HR-ANALYST");
  const isCgo = roleIds.includes("ROLE-CGO");
  const isBpReq = roleIds.includes("ROLE-BP-REQUESTER");
  const isFin = roleIds.includes("ROLE-FINANCE-ACCOUNTING");
  const isCon = roleIds.includes("ROLE-CONTRACTS-SUBCONTRACTS");
  const isPm = roleIds.includes("ROLE-PROJECT-MANAGER");
  const isAdmin = roleIds.includes("ROLE-ADMIN-SECURITY-INTEGRATION");

  const can =
    moduleIds.includes("charge-codes") ||
    isVpHr ||
    isHrAnalyst ||
    isCgo ||
    isBpReq ||
    isFin ||
    isCon ||
    isPm ||
    isAdmin;

  const tabs = useMemo(() => {
    const t: { id: FactoryTab; label: string }[] = [{ id: "catalog", label: "Catalog" }];
    if (isHrAnalyst || isVpHr) t.push({ id: "leave", label: "Leave / admin" });
    if (isBpReq || isCgo) t.push({ id: "bp", label: "B&P" });
    if (isFin) t.push({ id: "indirect", label: "G&A / indirect" });
    if (isCon || isFin || isPm) t.push({ id: "contract", label: "Contract hierarchy" });
    return t;
  }, [isHrAnalyst, isVpHr, isBpReq, isCgo, isFin, isCon, isPm]);

  if (!can) return <Navigate to={homePath} replace />;

  function ping(msg: string) {
    setFlash(msg);
    window.setTimeout(() => setFlash(""), 3500);
  }

  function draftLeave() {
    if (!isHrAnalyst && !isVpHr) return;
    if (leaveBin === "ST") {
      window.alert("Leave/admin codes must be PT, AT, or LWOP. Holiday/HT stays PT.");
      return;
    }
    const row: ChargeCode = {
      code: leaveCode.trim().toUpperCase(),
      title: leaveTitle.trim(),
      alias: leaveAlias.trim() || undefined,
      labor_type: leaveBin,
      class: "leave",
      lifecycle: "submitted",
      status: "closed",
      manager_name: "Riley Chen",
      manager_employee_id: "emp-vp-hr",
      company_id: DEMO_COMPANY_HFS.id,
      authorized_employee_ids: [],
    };
    setCodes((prev) => [row, ...prev.filter((c) => c.code !== row.code)]);
    ping(`Submitted ${row.code} for VP HR approval.`);
    setTab("catalog");
  }

  function approveLeave(code: string) {
    if (!isVpHr) return;
    setCodes((prev) =>
      prev.map((c) =>
        c.code === code
          ? { ...c, lifecycle: "open" as ChargeCodeLifecycle, status: "open" as const }
          : c,
      ),
    );
    ping(`Opened leave code ${code}.`);
  }

  function requestBp() {
    if (!isBpReq) return;
    const n =
      1 +
      codes.filter((c) => c.class === "bp").length;
    const code = `IND-BP-${String(n).padStart(4, "0")}`;
    const row: ChargeCode = {
      code,
      title: bpTitle.trim(),
      description: bpReason.trim() || "B&P pursuit",
      labor_type: "ST",
      class: "bp",
      lifecycle: "submitted",
      status: "closed",
      manager_name: "Alex Rivera",
      manager_employee_id: "emp-cgo",
      company_id: DEMO_COMPANY_HFS.id,
      authorized_employee_ids: [],
    };
    setCodes((prev) => [row, ...prev]);
    ping(`B&P request ${code} sent to CGO.`);
    setTab("catalog");
  }

  function approveBp(code: string) {
    if (!isCgo) return;
    setCodes((prev) =>
      prev.map((c) =>
        c.code === code
          ? { ...c, lifecycle: "open" as ChargeCodeLifecycle, status: "open" as const }
          : c,
      ),
    );
    ping(`CGO opened B&P code ${code}.`);
  }

  function createGa() {
    if (!isFin) return;
    const n = 1 + codes.filter((c) => c.class === "ga").length;
    const code = `IND-GA-${String(n).padStart(4, "0")}`;
    const row: ChargeCode = {
      code,
      title: gaTitle.trim(),
      labor_type: "ST",
      class: "ga",
      lifecycle: "open",
      status: "open",
      manager_name: "Morgan Blake",
      manager_employee_id: "emp-blake",
      company_id: DEMO_COMPANY_HFS.id,
      authorized_employee_ids: [],
    };
    setCodes((prev) => [row, ...prev]);
    ping(`Finance opened ${code}.`);
  }

  function draftContractHierarchy() {
    if (!isCon) return;
    const leaf = task.trim()
      ? `${vv}.${ccc}.${per}.${clin}.${task.trim().toUpperCase()}`
      : `${vv}.${ccc}.${per}.${clin}`;
    const row: ChargeCode = {
      code: leaf,
      title: contractTitle.trim(),
      description: `Draft from Contracts — pricing ${pricingType}; awaiting Finance then PM confirm.`,
      labor_type: "ST",
      class: "direct",
      lifecycle: "finance_review",
      status: "closed",
      manager_name: "Jordan Ortiz",
      manager_employee_id: "emp-ortiz",
      company_id: DEMO_COMPANY_HFS.id,
      authorized_employee_ids: [],
      pop_start: "2026-01-01",
      pop_end: "2027-12-31",
      award_value: Number(award) || 0,
      ceiling_value: Number(ceiling) || 0,
      funded_value: Number(funded) || 0,
    };
    setCodes((prev) => [row, ...prev.filter((c) => c.code !== row.code)]);
    ping(`Drafted ${leaf} → finance_review.`);
    setTab("catalog");
  }

  function financeApprove(code: string) {
    if (!isFin) return;
    setCodes((prev) =>
      prev.map((c) =>
        c.code === code ? { ...c, lifecycle: "pm_confirm" as ChargeCodeLifecycle } : c,
      ),
    );
    ping(`Finance approved ${code} → pm_confirm.`);
  }

  function pmConfirm(code: string) {
    if (!isPm) return;
    setCodes((prev) =>
      prev.map((c) =>
        c.code === code
          ? { ...c, lifecycle: "open" as ChargeCodeLifecycle, status: "open" as const }
          : c,
      ),
    );
    ping(`PM confirmed — ${code} is open for charging.`);
  }

  const classLabel = (c?: ChargeCodeClass) =>
    ({
      leave: "Leave/admin",
      bp: "B&P",
      ga: "G&A",
      other_indirect: "Other indirect",
      direct: "Direct",
    })[c || "direct"] || "—";

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <header className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-teal">
          REL-019 · DEC-CHARGE-001…004
        </p>
        <h1 className="mt-2 font-display text-3xl text-navy">Charge codes</h1>
        <p className="mt-2 max-w-3xl text-sm text-ink-muted leading-relaxed">
          Create leave codes (HR Analyst → VP HR), B&P (Requester → CGO), G&A/OH (Finance), and
          contract hierarchies <code className="text-navy">VV.CCC.PER.CLIN[.TASK]</code>{" "}
          (PER 000=base … 999; Contracts → Finance → PM confirm). Non-contract codes use short forms:{" "}
          <code className="text-navy">PT-*</code> / <code className="text-navy">IND-GA-####</code> /{" "}
          <code className="text-navy">IND-BP-####</code> / <code className="text-navy">IND-OH-####</code>
          — not the long contract number. Access requests stay on Timekeeping.
        </p>
        {flash && (
          <p className="mt-3 rounded-lg bg-brand-teal/10 px-3 py-2 text-sm text-navy">{flash}</p>
        )}
      </header>

      <div className="flex flex-wrap gap-2 border-b border-navy-100 pb-3 mb-6">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={
              tab === t.id
                ? "rounded-lg bg-navy-900 px-3 py-1.5 text-sm font-semibold text-white"
                : "rounded-lg px-3 py-1.5 text-sm font-medium text-ink-muted hover:bg-navy-50"
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "catalog" && (
        <div className="overflow-x-auto rounded-xl border border-navy-100">
          <table className="w-full text-left text-sm">
            <thead className="bg-navy-50 text-xs uppercase tracking-wide text-ink-subtle">
              <tr>
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2">Title</th>
                <th className="px-3 py-2">Class</th>
                <th className="px-3 py-2">Lifecycle</th>
                <th className="px-3 py-2">Funding</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => (
                <tr key={c.code} className="border-t border-navy-100 align-top">
                  <td className="px-3 py-2 font-mono text-xs text-navy">
                    {c.code}
                    {c.alias ? (
                      <span className="ml-1 text-ink-subtle">({c.alias})</span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-navy">{c.title}</div>
                    <div className="text-xs text-ink-subtle">
                      {c.labor_type} · {c.status}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-ink-muted">{classLabel(c.class)}</td>
                  <td className="px-3 py-2 text-ink-muted">{c.lifecycle || c.status}</td>
                  <td className="px-3 py-2 text-xs text-ink-muted">
                    {c.class === "direct" ? (
                      <>
                        A {fmtMoney(c.award_value)}
                        <br />
                        C {fmtMoney(c.ceiling_value)}
                        <br />
                        F {fmtMoney(c.funded_value)}
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-3 py-2 space-y-1">
                    {isVpHr && c.class === "leave" && c.lifecycle === "submitted" && (
                      <button
                        type="button"
                        className="block text-xs font-semibold text-brand-teal"
                        onClick={() => approveLeave(c.code)}
                      >
                        VP HR approve/open
                      </button>
                    )}
                    {isCgo && c.class === "bp" && c.lifecycle === "submitted" && (
                      <button
                        type="button"
                        className="block text-xs font-semibold text-brand-teal"
                        onClick={() => approveBp(c.code)}
                      >
                        CGO approve/open
                      </button>
                    )}
                    {isFin && c.class === "direct" && c.lifecycle === "finance_review" && (
                      <button
                        type="button"
                        className="block text-xs font-semibold text-brand-teal"
                        onClick={() => financeApprove(c.code)}
                      >
                        Finance approve
                      </button>
                    )}
                    {isPm && c.class === "direct" && c.lifecycle === "pm_confirm" && (
                      <button
                        type="button"
                        className="block text-xs font-semibold text-brand-teal"
                        onClick={() => pmConfirm(c.code)}
                      >
                        PM confirm/open
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "leave" && (
        <section className="max-w-xl space-y-4 rounded-xl border border-navy-100 p-5">
          <h2 className="font-display text-xl text-navy">Leave / admin code draft</h2>
          <p className="text-sm text-ink-muted">
            HR Analyst drafts; VP HR opens. Holiday/HT remains a PT code (alias allowed).
          </p>
          <label className="block text-sm">
            Code
            <input
              className="mt-1 w-full rounded-lg border border-navy-100 px-3 py-2"
              value={leaveCode}
              onChange={(e) => setLeaveCode(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            Title
            <input
              className="mt-1 w-full rounded-lg border border-navy-100 px-3 py-2"
              value={leaveTitle}
              onChange={(e) => setLeaveTitle(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            Labor bin
            <select
              className="mt-1 w-full rounded-lg border border-navy-100 px-3 py-2"
              value={leaveBin}
              onChange={(e) => setLeaveBin(e.target.value as LaborType)}
            >
              <option value="PT">PT</option>
              <option value="AT">AT</option>
              <option value="LWOP">LWOP</option>
            </select>
          </label>
          <label className="block text-sm">
            Alias (e.g. HT)
            <input
              className="mt-1 w-full rounded-lg border border-navy-100 px-3 py-2"
              value={leaveAlias}
              onChange={(e) => setLeaveAlias(e.target.value)}
            />
          </label>
          <button
            type="button"
            disabled={!isHrAnalyst && !isVpHr}
            onClick={draftLeave}
            className="rounded-lg bg-navy-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
          >
            Submit to VP HR
          </button>
        </section>
      )}

      {tab === "bp" && (
        <section className="max-w-xl space-y-4 rounded-xl border border-navy-100 p-5">
          <h2 className="font-display text-xl text-navy">B&P charge code</h2>
          <p className="text-sm text-ink-muted">
            Requesters submit; CGO alone opens. Job profiles below auto-grant B&P Requester
            (removable).
          </p>
          {(isCgo || isAdmin || isBpReq) && (
            <ul className="space-y-2 text-sm">
              {JOB_AUTO_BP.map((job) => (
                <li key={job} className="flex items-center justify-between gap-3">
                  <span>
                    {job} → ROLE-BP-REQUESTER
                  </span>
                  <button
                    type="button"
                    className="text-xs font-semibold text-brand-teal"
                    onClick={() =>
                      setBpAutoGrant((g) => ({ ...g, [job]: !g[job] }))
                    }
                  >
                    {bpAutoGrant[job] ? "Granted (click to remove)" : "Removed (click to restore)"}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {isBpReq && (
            <>
              <label className="block text-sm">
                Title
                <input
                  className="mt-1 w-full rounded-lg border border-navy-100 px-3 py-2"
                  value={bpTitle}
                  onChange={(e) => setBpTitle(e.target.value)}
                />
              </label>
              <label className="block text-sm">
                Justification
                <textarea
                  className="mt-1 w-full rounded-lg border border-navy-100 px-3 py-2"
                  rows={3}
                  value={bpReason}
                  onChange={(e) => setBpReason(e.target.value)}
                />
              </label>
              <button
                type="button"
                onClick={requestBp}
                className="rounded-lg bg-navy-900 px-4 py-2 text-sm font-semibold text-white"
              >
                Request B&P code
              </button>
            </>
          )}
          {!isBpReq && isCgo && (
            <p className="text-sm text-ink-muted">
              Switch to Catalog to approve submitted B&P requests, or add B&P Requester to your
              role preview to submit one.
            </p>
          )}
        </section>
      )}

      {tab === "indirect" && isFin && (
        <section className="max-w-xl space-y-4 rounded-xl border border-navy-100 p-5">
          <h2 className="font-display text-xl text-navy">G&A / other indirect</h2>
          <p className="text-sm text-ink-muted">
            Finance opens G&A codes. B&P stays with CGO.
          </p>
          <label className="block text-sm">
            Title
            <input
              className="mt-1 w-full rounded-lg border border-navy-100 px-3 py-2"
              value={gaTitle}
              onChange={(e) => setGaTitle(e.target.value)}
            />
          </label>
          <button
            type="button"
            onClick={createGa}
            className="rounded-lg bg-navy-900 px-4 py-2 text-sm font-semibold text-white"
          >
            Open G&A code
          </button>
        </section>
      )}

      {tab === "contract" && (
        <section className="max-w-2xl space-y-4 rounded-xl border border-navy-100 p-5">
          <h2 className="font-display text-xl text-navy">Contract hierarchy</h2>
          <p className="text-sm text-ink-muted">
            Contracts drafts <code>VV.CCC.PER.CLIN[.TASK]</code> with award / ceiling / funded and
            CLIN pricing type; Finance approves; PM must confirm before open.
          </p>
          {isCon && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              {[
                ["VV", vv, setVv],
                ["CCC", ccc, setCcc],
                ["PER", per, setPer],
                ["CLIN", clin, setClin],
                ["TASK", task, setTask],
              ].map(([label, val, set]) => (
                <label key={label as string} className="block text-xs">
                  {label as string}
                  <input
                    className="mt-1 w-full rounded-lg border border-navy-100 px-2 py-1.5 font-mono text-sm"
                    value={val as string}
                    onChange={(e) => (set as (v: string) => void)(e.target.value)}
                  />
                </label>
              ))}
            </div>
          )}
          {isCon && (
            <>
              <label className="block text-sm">
                Title
                <input
                  className="mt-1 w-full rounded-lg border border-navy-100 px-3 py-2"
                  value={contractTitle}
                  onChange={(e) => setContractTitle(e.target.value)}
                />
              </label>
              <label className="block text-sm">
                CLIN pricing type
                <select
                  className="mt-1 w-full rounded-lg border border-navy-100 px-3 py-2"
                  value={pricingType}
                  onChange={(e) => setPricingType(e.target.value)}
                >
                  {["FFP", "T&M", "CPFF", "CPIF", "CPAF", "LH", "OTHER"].map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </label>
              <div className="grid grid-cols-3 gap-3">
                <label className="block text-xs">
                  Award
                  <input
                    className="mt-1 w-full rounded-lg border border-navy-100 px-2 py-1.5"
                    value={award}
                    onChange={(e) => setAward(e.target.value)}
                  />
                </label>
                <label className="block text-xs">
                  Ceiling
                  <input
                    className="mt-1 w-full rounded-lg border border-navy-100 px-2 py-1.5"
                    value={ceiling}
                    onChange={(e) => setCeiling(e.target.value)}
                  />
                </label>
                <label className="block text-xs">
                  Funded
                  <input
                    className="mt-1 w-full rounded-lg border border-navy-100 px-2 py-1.5"
                    value={funded}
                    onChange={(e) => setFunded(e.target.value)}
                  />
                </label>
              </div>
              <p className="text-xs text-ink-subtle font-mono">
                Preview: {vv}.{ccc}.{per}.{clin}
                {task.trim() ? `.${task.trim().toUpperCase()}` : ""}
              </p>
              <button
                type="button"
                onClick={draftContractHierarchy}
                className="rounded-lg bg-navy-900 px-4 py-2 text-sm font-semibold text-white"
              >
                Draft → Finance review
              </button>
            </>
          )}
          {!isCon && (
            <p className="text-sm text-ink-muted">
              Use Catalog actions for Finance approve / PM confirm, or switch role preview to
              Contracts to draft a new hierarchy.
            </p>
          )}
        </section>
      )}
    </div>
  );
}
